/**
 * Generate code snippets for video streaming API deployment
 */

import { WorkflowParameter } from "./types";

export type VideoSourceType = "webcam" | "rtsp" | "video_file";
export type VideoStreamingOutputs = { streamOutputName?: string | null; dataOutputNames: string[] };

export const PYTHON_INSTALL_CODE = `
\`\`\`bash
pip install -U inference-sdk
\`\`\`
`;

export const JS_INSTALL_CODE = `
\`\`\`bash
npm install @roboflow/inference-sdk
\`\`\`
`;

const DEFAULT_DATA_OUTPUTS = ["predictions"];

function generatePythonParametersArg(workflowParameters: WorkflowParameter[]): string {
    if (workflowParameters.length === 0) return "";

    const pythonParameterString = workflowParameters
        .map((param) => {
            const value =
                param.type === "boolean"
                    ? param.parsed_value
                        ? "True"
                        : "False"
                    : JSON.stringify(param.parsed_value);
            return `        "${param.name}": ${value}`;
        })
        .join(",\n");

    return `,
    workflow_parameters={
${pythonParameterString}
    }`;
}

function generateJavaScriptParametersArg(workflowParameters: WorkflowParameter[]): string {
    if (workflowParameters.length === 0) return "";

    const jsParameterString = workflowParameters
        .map((param) => {
            const value = JSON.stringify(param.parsed_value);
            return `                    ${JSON.stringify(param.name)}: ${value}`;
        })
        .join(",\n");

    return `,
                workflowsParameters: {
${jsParameterString}
                }`;
}

export function generateVideoApiPythonCode(
    workflowUrl: string,
    workspaceUrl: string,
    apiKey: string,
    workflowParameters: WorkflowParameter[],
    sourceType: VideoSourceType = "webcam",
    serverUrl: string = "https://serverless.roboflow.com",
    outputs?: VideoStreamingOutputs,
    isLocal: boolean = false
): string {
    const parametersArg = generatePythonParametersArg(workflowParameters);
    const streamOutputName = outputs?.streamOutputName;
    const dataOutputNames = outputs?.dataOutputNames;

    const streamOutputArg = streamOutputName
        ? `    stream_output=["${streamOutputName}"],  # Get video back with annotations`
        : `    # stream_output=["my_stream_output"], # Uncomment and check your stream output name`;

    const dataOutputArg = dataOutputNames?.length
        ? `    data_output=${JSON.stringify(dataOutputNames)}      # Get prediction data via datachannel`
        : `    # data_output=["predictions"] # Uncomment and check your data output name`;

    if (sourceType === "webcam") {
        return `
\`\`\`python
import cv2
from inference_sdk import InferenceHTTPClient
from inference_sdk.webrtc import WebcamSource, StreamConfig, VideoMetadata

# Initialize client
client = InferenceHTTPClient.init(
    api_url="${serverUrl}",
    api_key="${apiKey}"
)

# Configure video source (webcam)
source = WebcamSource(resolution=(1280, 720))

# Configure streaming options
config = StreamConfig(
${streamOutputArg}
${dataOutputArg},
    processing_timeout=3600              # 60 minutes${
        isLocal
            ? ""
            : `,
    requested_plan="webrtc-gpu-medium",  # Options: webrtc-gpu-small, webrtc-gpu-medium, webrtc-gpu-large
    requested_region="us"                # Options: us, eu, ap`
    }
)

# Create streaming session
session = client.webrtc.stream(
    source=source,
    workflow="${workflowUrl}",
    workspace="${workspaceUrl}",
    image_input="image",
    config=config${parametersArg}
)

# Handle incoming video frames
@session.on_frame
def show_frame(frame, metadata):
    cv2.imshow("Workflow Output", frame)
    if cv2.waitKey(1) & 0xFF == ord("q"):
        session.close()

# Handle prediction data via datachannel
@session.on_data()
def on_data(data: dict, metadata: VideoMetadata):
    print(f"Frame {metadata.frame_id}: {data}")

# Run the session (blocks until closed)
session.run()
\`\`\`
`;
    } else if (sourceType === "rtsp") {
        return `
\`\`\`python
import cv2
from inference_sdk import InferenceHTTPClient
from inference_sdk.webrtc import RTSPSource, StreamConfig, VideoMetadata

# Initialize client
client = InferenceHTTPClient.init(
    api_url="${serverUrl}",
    api_key="${apiKey}"
)

# Configure video source (RTSP stream)
source = RTSPSource("rtsp://demo.roboflow.com:8554")

# Configure streaming options
config = StreamConfig(
${streamOutputArg}
${dataOutputArg},
    processing_timeout=3600              # 60 minutes${
        isLocal
            ? ""
            : `,
    requested_plan="webrtc-gpu-medium",  # Options: webrtc-gpu-small, webrtc-gpu-medium, webrtc-gpu-large
    requested_region="us"                # Options: us, eu, ap`
    }
)

# Create streaming session
session = client.webrtc.stream(
    source=source,
    workflow="${workflowUrl}",
    workspace="${workspaceUrl}",
    image_input="image",
    config=config${parametersArg}
)

# Handle incoming video frames
@session.on_frame
def show_frame(frame, metadata):
    cv2.imshow("Workflow Output", frame)
    if cv2.waitKey(1) & 0xFF == ord("q"):
        session.close()

# Handle prediction data via datachannel
@session.on_data()
def on_data(data: dict, metadata: VideoMetadata):
    print(f"Frame {metadata.frame_id}: {data}")

# Run the session (blocks until closed)
session.run()
\`\`\`
`;
    } else {
        const streamOutput = streamOutputName || null;
        const dataOutputs = dataOutputNames?.length ? dataOutputNames : DEFAULT_DATA_OUTPUTS;

        // In video_file mode, we need to pass video streams in data_output to get them
        const allOutputs: string[] = [];
        if (streamOutput) allOutputs.push(streamOutput);
        allOutputs.push(...dataOutputs.filter((o) => o !== streamOutput));

        const allOutputsPython = JSON.stringify(allOutputs);

        return `
\`\`\`python
import cv2
import base64
import numpy as np
from inference_sdk import InferenceHTTPClient
from inference_sdk.webrtc import VideoFileSource, StreamConfig, VideoMetadata

client = InferenceHTTPClient.init(
    api_url="${serverUrl}",
    api_key="${apiKey}"
)

source = VideoFileSource("path/to/your/video.mp4", realtime_processing=False)  # Buffer and process all frames

VIDEO_OUTPUT = ${streamOutput ? `"${streamOutput}"` : "None"}
DATA_OUTPUTS = ${JSON.stringify(dataOutputs)}

config = StreamConfig(
    stream_output=[], # We request all data via data_output for video files
    data_output=${allOutputsPython}${
        isLocal
            ? ""
            : `,
    requested_plan="webrtc-gpu-medium",
    requested_region="us",`
    }
)

session = client.webrtc.stream(
    source=source,
    workflow="${workflowUrl}",
    workspace="${workspaceUrl}",
    image_input="image",
    config=config${parametersArg}
)

frames = []

@session.on_data()
def on_data(data: dict, metadata: VideoMetadata):
    # print(f"Frame {metadata.frame_id} predictions: {data}")
    
    if VIDEO_OUTPUT and VIDEO_OUTPUT in data:
        timestamp_ms = metadata.pts * metadata.time_base * 1000
        img = cv2.imdecode(np.frombuffer(base64.b64decode(data[VIDEO_OUTPUT]["value"]), np.uint8), cv2.IMREAD_COLOR)
        frames.append((timestamp_ms, metadata.frame_id, img))
        print(f"Processed frame {metadata.frame_id}")
    else:
        print(f"Processed frame {metadata.frame_id} (data only)")

session.run()

if VIDEO_OUTPUT and len(frames) > 0:
    # Stitch frames into output video
    frames.sort(key=lambda x: x[1])
    fps = (len(frames) - 1) / ((frames[-1][0] - frames[0][0]) / 1000) if len(frames) > 1 else 30.0
    h, w = frames[0][2].shape[:2]
    out = cv2.VideoWriter("output.mp4", cv2.VideoWriter_fourcc(*"mp4v"), fps, (w, h))
    for _, _, frame in frames:
        out.write(frame)
    out.release()
    print(f"Done! {len(frames)} frames at {fps:.1f} FPS -> output.mp4")
elif VIDEO_OUTPUT:
    print("No video frames collected.")
\`\`\`
`;
    }
}

export function generateVideoApiJavaScriptCode(
    workflowUrl: string,
    workspaceUrl: string,
    apiKey: string,
    workflowParameters: WorkflowParameter[],
    sourceType: VideoSourceType = "webcam",
    serverUrl: string = "https://serverless.roboflow.com",
    outputs?: VideoStreamingOutputs,
    isLocal: boolean = false
): string {
    const parametersArg = generateJavaScriptParametersArg(workflowParameters);
    const streamOutput = outputs?.streamOutputName || null;
    const dataOutputs = outputs?.dataOutputNames?.length
        ? outputs.dataOutputNames
        : DEFAULT_DATA_OUTPUTS;
    const streamOutputJS = streamOutput ? JSON.stringify([streamOutput]) : "[]";
    const dataOutputJS = JSON.stringify(dataOutputs);

    if (sourceType === "webcam") {
        return `
\`\`\`jsx
import { useRef, useState } from "react";
import { connectors, webrtc, streams } from "@roboflow/inference-sdk";

export function WorkflowStream() {
    const videoRef = useRef(null);
    const connectionRef = useRef(null);
    const [streaming, setStreaming] = useState(false);
    const [connecting, setConnecting] = useState(false);

    async function start() {
        setConnecting(true);
        // ⚠️ Testing only - exposes API key in frontend. Use a backend proxy in production (see Step 4)
        const connector = connectors.withApiKey("${apiKey}", { serverUrl: "${serverUrl}" });
        const stream = await streams.useCamera({ video: { facingMode: "environment" } });

        connectionRef.current = await webrtc.useStream({
            source: stream,
            connector,
            wrtcParams: {
                workspaceName: "${workspaceUrl}",
                workflowId: "${workflowUrl}",
                streamOutputNames: ${streamOutputJS},
                dataOutputNames: ${dataOutputJS},
                processingTimeout: 3600${
                    isLocal
                        ? ""
                        : `,
                requestedPlan: "webrtc-gpu-medium",  // Options: webrtc-gpu-small, webrtc-gpu-medium, webrtc-gpu-large
                requestedRegion: "us"  // Options: us, eu, ap`
                }${parametersArg}
            },
            onData: (data) => console.log("Predictions:", data)
        });

        videoRef.current.srcObject = await connectionRef.current.remoteStream();
        setStreaming(true);
        setConnecting(false);
    }

    function stop() {
        connectionRef.current?.cleanup();
        videoRef.current.srcObject = null;
        setStreaming(false);
    }

    return (
        <div>
            <video ref={videoRef} autoPlay playsInline muted style={{ width: 640, height: 480 }} />
            <button onClick={streaming ? stop : start} disabled={connecting}>
                {connecting ? "Connecting..." : streaming ? "Stop" : "Start"}
            </button>
        </div>
    );
}
\`\`\`
`;
    } else if (sourceType === "rtsp") {
        return `
\`\`\`jsx
import { useRef, useState } from "react";
import { connectors, webrtc } from "@roboflow/inference-sdk";

export function WorkflowStream() {
    const videoRef = useRef(null);
    const connectionRef = useRef(null);
    const [streaming, setStreaming] = useState(false);
    const [connecting, setConnecting] = useState(false);

    async function start() {
        setConnecting(true);
        // ⚠️ Testing only - exposes API key in frontend. Use a backend proxy in production (see Step 4)
        const connector = connectors.withApiKey("${apiKey}", { serverUrl: "${serverUrl}" });

        connectionRef.current = await webrtc.useRtspStream({
            rtspUrl: "rtsp://demo.roboflow.com:8554",
            connector,
            wrtcParams: {
                workspaceName: "${workspaceUrl}",
                workflowId: "${workflowUrl}",
                streamOutputNames: ${streamOutputJS},
                dataOutputNames: ${dataOutputJS},
                processingTimeout: 3600${
                    isLocal
                        ? ""
                        : `,
                requestedPlan: "webrtc-gpu-medium",  // Options: webrtc-gpu-small, webrtc-gpu-medium, webrtc-gpu-large
                requestedRegion: "us"  // Options: us, eu, ap`
                }${parametersArg}
            },
            onData: (data) => console.log("Predictions:", data)
        });

        videoRef.current.srcObject = await connectionRef.current.remoteStream();
        setStreaming(true);
        setConnecting(false);
    }

    function stop() {
        connectionRef.current?.cleanup();
        videoRef.current.srcObject = null;
        setStreaming(false);
    }

    return (
        <div>
            <video ref={videoRef} autoPlay playsInline muted style={{ width: 640, height: 480 }} />
            <button onClick={streaming ? stop : start} disabled={connecting}>
                {connecting ? "Connecting..." : streaming ? "Stop" : "Start"}
            </button>
        </div>
    );
}
\`\`\`
`;
    } else {
        // video_file
        return `
\`\`\`jsx
import { useRef, useState } from "react";
import { connectors, webrtc } from "@roboflow/inference-sdk";

export function WorkflowStream() {
    const connectionRef = useRef(null);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [frameCount, setFrameCount] = useState(0);
    const [currentFrame, setCurrentFrame] = useState(null);

    async function processFile(file) {
        setProcessing(true);
        setFrameCount(0);
        // ⚠️ Testing only - exposes API key in frontend. Use a backend proxy in production (see Step 4)
        const connector = connectors.withApiKey("${apiKey}", { serverUrl: "${serverUrl}" });

        const VIDEO_OUTPUT = "${streamOutput}";
        connectionRef.current = await webrtc.useVideoFile({
            file,
            connector,
            wrtcParams: {
                workspaceName: "${workspaceUrl}",
                workflowId: "${workflowUrl}",
                streamOutputNames: [],
                dataOutputNames: [VIDEO_OUTPUT, ...${dataOutputJS}].filter((v, i, a) => a.indexOf(v) === i),
                processingTimeout: 3600,${
                    isLocal
                        ? ""
                        : `,
                requestedPlan: "webrtc-gpu-medium",  // Options: webrtc-gpu-small, webrtc-gpu-medium, webrtc-gpu-large
                requestedRegion: "us",  // Options: us, eu, ap`
                }
                realtimeProcessing: false${parametersArg}
            },
            onData: (data) => {
                setFrameCount(n => n + 1);
                const viz = data.serialized_output_data?.[VIDEO_OUTPUT];
                if (viz?.value) setCurrentFrame(\`data:image/jpeg;base64,\${viz.value}\`);
            },
            onUploadProgress: (sent, total) => setProgress(Math.round(sent / total * 100)),
            onComplete: () => setProcessing(false)
        });
    }

    return (
        <div>
            {currentFrame ? (
                <img src={currentFrame} alt="Frame" style={{ width: 640 }} />
            ) : (
                <div style={{ width: 640, height: 480, background: "#000" }} />
            )}
            <input type="file" accept="video/*" onChange={e => e.target.files?.[0] && processFile(e.target.files[0])} disabled={processing} />
            {processing && <p>Upload: {progress}% | Frames: {frameCount}</p>}
            {!processing && frameCount > 0 && <p>✓ Processed {frameCount} frames</p>}
        </div>
    );
}
\`\`\`
`;
    }
}

export function getProductionConnectorSnippet(): string {
    return `
\`\`\`jsx
// Replace connectors.withApiKey(...) with:
const connector = connectors.withProxyUrl("/api/init-webrtc");
\`\`\`
`;
}

export function getProductionBackendSnippet(): string {
    return `
\`\`\`javascript
import express from "express";
import { InferenceHTTPClient } from "@roboflow/inference-sdk";

const app = express();
app.use(express.json());

app.post("/api/init-webrtc", async (req, res) => {
    const { offer, wrtcParams } = req.body;

    const client = InferenceHTTPClient.init({
        apiKey: process.env.ROBOFLOW_API_KEY
    });

    const answer = await client.initializeWebrtcWorker({
        offer,
        workspaceName: wrtcParams.workspaceName,
        workflowId: wrtcParams.workflowId,
        config: {
            streamOutputNames: wrtcParams.streamOutputNames,
            dataOutputNames: wrtcParams.dataOutputNames,
            workflowsParameters: wrtcParams.workflowsParameters,
            requestedPlan: wrtcParams.requestedPlan,
            requestedRegion: wrtcParams.requestedRegion,
            realtimeProcessing: wrtcParams.realtimeProcessing
        }
    });

    res.json(answer);
});

app.listen(3001, () => console.log("Proxy running on :3001"));
\`\`\`
`;
}
